import { useTranslation } from 'react-i18next';
import { CheckCircle2, MoreHorizontal, RefreshCw, Send, Trash2, Undo2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  useDeleteApplePayPlatformDomain,
  useProbeApplePayPlatformDomain,
  useRegisterApplePayPlatformDomain,
  useSetApplePayPlatformDomainActive,
  useUnregisterApplePayPlatformDomain,
} from '@/lib/api/apple-pay-platform';

import { DiagnoseDomainDialog } from './DiagnoseDomainDialog';
import type { PlatformDomain } from './types';

/**
 * One primary button plus an overflow menu.
 *
 * The lifecycle is strictly ordered — check the file, register with Apple, go
 * live — so at any moment exactly one action is the right next step. Showing
 * all five as equal buttons made every row a quiz; this makes the next move the
 * only prominent control and files the rest away.
 */
export function DomainRowActions({ domain }: { domain: PlatformDomain }) {
  const { t } = useTranslation('manageApplePay');
  const probe = useProbeApplePayPlatformDomain();
  const register = useRegisterApplePayPlatformDomain();
  const unregister = useUnregisterApplePayPlatformDomain();
  const setActive = useSetApplePayPlatformDomainActive();
  const remove = useDeleteApplePayPlatformDomain();

  const isBusy =
    probe.isPending || register.isPending || unregister.isPending || setActive.isPending || remove.isPending;

  const isRegistered = domain.status === 'registered';

  const primary = !isRegistered
    ? domain.status === 'file_ready'
      ? {
          label: t('domains.actions.register'),
          icon: Send,
          run: () => register.mutate({ domainId: domain.id }),
        }
      : {
          label: t('domains.actions.probe'),
          icon: RefreshCw,
          run: () => probe.mutate({ domainId: domain.id }),
        }
    : domain.active
      ? null
      : {
          label: t('domains.actions.activate'),
          icon: CheckCircle2,
          run: () => setActive.mutate({ domainId: domain.id, active: true }),
        };

  return (
    <div className="flex items-center justify-end gap-1.5">
      {/* Always available, including on a row that looks perfect — a healthy row
          with a stale funnel snapshot is exactly the case this exists for. */}
      <DiagnoseDomainDialog domain={domain} />

      {primary && (
        <Button size="sm" variant="outline" disabled={isBusy} onClick={primary.run}>
          <primary.icon className="mr-1.5 h-3.5 w-3.5" />
          {primary.label}
        </Button>
      )}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="ghost" disabled={isBusy} aria-label={t('domains.columns.actions')}>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => probe.mutate({ domainId: domain.id })}>
            <RefreshCw className="mr-2 h-3.5 w-3.5" />
            {t('domains.actions.probe')}
          </DropdownMenuItem>

          {/* Available on a registered row too. Re-registering is idempotent —
              it re-probes, re-sends the hosts that serve the file, and reuses
              the merchant's existing Basis Theory registration — and it is the
              only way to retry the half that can fail after Apple accepts the
              domain. Hiding it left kupid.ai registered, unable to mint a
              session, and displaying an error telling the operator to retry
              registration with no button to do it. */}
          <DropdownMenuItem onSelect={() => register.mutate({ domainId: domain.id })}>
            <Send className="mr-2 h-3.5 w-3.5" />
            {isRegistered ? t('domains.actions.reregister') : t('domains.actions.register')}
          </DropdownMenuItem>

          {isRegistered && domain.active && (
            <DropdownMenuItem onSelect={() => setActive.mutate({ domainId: domain.id, active: false })}>
              <Undo2 className="mr-2 h-3.5 w-3.5" />
              {t('domains.actions.deactivate')}
            </DropdownMenuItem>
          )}

          {isRegistered && (
            <DropdownMenuItem onSelect={() => unregister.mutate({ domainId: domain.id })}>
              <Undo2 className="mr-2 h-3.5 w-3.5" />
              {t('domains.actions.unregister')}
            </DropdownMenuItem>
          )}

          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onSelect={() => {
              if (window.confirm(t('domains.deleteConfirm', { domain: domain.domain }))) {
                remove.mutate({ domainId: domain.id });
              }
            }}
          >
            <Trash2 className="mr-2 h-3.5 w-3.5" />
            {t('domains.actions.delete')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
